import { useState } from 'react';
import PropTypes from 'prop-types';

import { TextInput } from '@mantine/core';

import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { radius } from 'theme/radius';

// Per-country display hints. Falls back to a generic international example so a
// caller can pass any region without the placeholder/error message going stale.
//
// Every example here MUST pass the libphonenumber-js check below, or the field
// rejects the very number it just told the host to type. `555` reads as the
// safe fictional prefix, but it is only reserved as an *exchange* — as an area
// code it is unassigned in the NANP, so `(555) 123-4567` and `+15551234567`
// both fail isValid(). The fix is a real area code with the reserved 555-01XX
// line range: `(212) 555-0147` validates and still cannot be anyone's number.
const COUNTRY_EXAMPLES = {
  US: { placeholder: '(212) 555-0147', example: '(212) 555-0147 or +12125550147' },
  AU: { placeholder: '0412 345 678', example: '0412345678 or +61412345678' }
};

const PhoneInput = ({
  value,
  onChange,
  handleError,
  handleFormattedValue,
  required = false,
  disableCustomTheme = false,
  defaultCountry = 'US',
  ...other
}) => {
  const [phoneNumber, setPhoneNumber] = useState(value);
  const [error, setError] = useState(null);

  // Same rule as COUNTRY_EXAMPLES above: the fallback has to validate too.
  const hints = COUNTRY_EXAMPLES[defaultCountry] || { placeholder: '+1 212 555 0147', example: '+1 212 555 0147' };

  const handleChange = (e) => {
    const input = e.target.value;
    setPhoneNumber(input);

    if (onChange) {
      onChange(input);
    }

    if (!input && !required) {
      setError(null);
      if (handleError) handleError(null);
      if (handleFormattedValue) handleFormattedValue(null);
      return;
    }

    // Validate non-empty input or required field
    const phoneNumberObj = parsePhoneNumberFromString(input, defaultCountry);
    if (phoneNumberObj && phoneNumberObj.isValid()) {
      setError(null);
      if (handleError) handleError(null);
      if (handleFormattedValue) handleFormattedValue(phoneNumberObj.number);
    } else {
      setError('Invalid phone number');
      if (handleError) handleError('Invalid phone number');
      if (handleFormattedValue) handleFormattedValue(null);
    }
  };

  return (
    <TextInput
      label="Phone Number"
      type="tel"
      value={phoneNumber}
      onChange={handleChange}
      error={error ? `Enter a valid phone number (e.g., ${hints.example})` : undefined}
      placeholder={hints.placeholder}
      required={required}
      radius={radius.control}
      styles={
        !disableCustomTheme
          ? {
              root: {
                marginTop: 8,
                marginBottom: 8
              }
            }
          : undefined
      }
      {...other}
    />
  );
};

PhoneInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  handleError: PropTypes.func,
  handleFormattedValue: PropTypes.func,
  required: PropTypes.bool,
  disableCustomTheme: PropTypes.bool,
  defaultCountry: PropTypes.string
};

PhoneInput.defaultProps = {
  required: false,
  disableCustomTheme: false,
  defaultCountry: 'US'
};

export default PhoneInput;
